import type React from "react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import AvailableFeatureCounter from "src/common/components/AvailableCounter/AvailableFeatureCounter";
import { Checkbox } from "src/common/components/Input";
import TableComp from "src/common/components/Table/Table";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { RootState } from "src/common/state/store";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { getCurrentUsersFromCsv } from "src/features/projectGroups/state/projectGroupsSlice";
import { CONFIRM_CSV_COMPONENT } from "src/features/team/components/ParticipantFromCsvModal";
import { getSubscriptionFeature } from "src/features/subscription/state/subscriptionSlice";
import {
  bulkInviteEndusersWithoutProject,
  getEndusers,
  inviteExternalEnduserWithoutProject,
} from "src/features/team/state/teamSlice";
import { FooterButtons } from "src/common/components/FooterButtons";
import { trackEvent } from "src/trackEvent";

const PARTICIPANTS_CSV_IMPORT_COMPONENT = "participantsCsvImportComponent";

// Helper to validate users from CSV
const isValidCsvUser = (user: any) => {
  if (
    !user ||
    typeof user !== "object" ||
    typeof user.email !== "string" ||
    typeof user.firstname !== "string" ||
    typeof user.lastname !== "string"
  ) {
    return false;
  }
  return isValidEmail(user.email);
};

const isValidEmail = (email: string) => {
  // Basic email RFC 5322-ish regex (not perfect but good enough for UI level)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const ParticipantsCsvImport: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { handleSubmit } = useForm();

  const usersFromCsv = useAppSelector(getCurrentUsersFromCsv);
  const participants = useAppSelector(getEndusers);
  const participantsFeature = useAppSelector((state: RootState) =>
    getSubscriptionFeature(state, "number-of-available-enduser-accounts"),
  );

  const [isCheckAll, setIsCheckAll] = useState(false);
  const [isCheck, setIsCheck] = useState<string[]>([]);
  const [teamList, setTeamList] = useState<any[]>([]);
  const [tableHeight, setTableHeight] = useState(0);

  const currentNumberOfAvailableParticipants = participantsFeature
    ? participantsFeature.QuantityCurrent - isCheck.length
    : 0;
  const totalNumberOfAvailableParticipants = participantsFeature
    ? participantsFeature.QuantityCurrent + participants.length
    : -1;
  const isOverLimit = currentNumberOfAvailableParticipants < 0;

  const deduceNextDisabledTooltip = () => {
    if (
      isOverLimit ||
      participants.length + isCheck.length >
        (participantsFeature ? participantsFeature.QuantityCurrent + participants.length : 0)
    ) {
      return t("tooltip.participantsLimitExceeded");
    }
    if (isCheck.length === 0) {
      return t("tooltip.pleaseSelectAtLeastOneParticipant");
    }
    return "";
  };

  // Handle resizing
  useEffect(() => {
    const calculateHeight = () => {
      const h = Math.round(window.innerHeight * (3 / 5));
      setTableHeight(h);
    };
    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, []);

  // Validate and filter CSV data, robustly
  useEffect(() => {
    try {
      if (!Array.isArray(usersFromCsv)) {
        throw new Error("CSV did not parse into an array");
      }
      const validUsers = usersFromCsv.filter(isValidCsvUser);
      const invalidCount = usersFromCsv.length - validUsers.length;

      if (invalidCount > 0) {
        dispatch(
          setAlert({
            type: "warning",
            customTimeoutInMs: 10000,
            msg: t("alerts.invalidCsvEntries", { count: invalidCount }),
          }),
        );
      }

      if (validUsers.length === 0) {
        dispatch(
          setAlert({
            type: "warning",
            customTimeoutInMs: 10000,
            msg: t("alerts.noValidUsersFound"),
          }),
        );
        navigate("/learners/new");
        return;
      }

      setTeamList(validUsers);
    } catch (err) {
      console.error("CSV processing failed:", err);
      dispatch(
        setAlert({
          type: "error",
          customTimeoutInMs: 10000,
          msg: t("alerts.csvProcessingFailed"),
        }),
      );
      navigate("/learners/new");
    }
  }, [usersFromCsv, dispatch, t, navigate]);

  // Type‐safe checkbox change handler
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const { id, checked } = e.target;
    setIsCheck((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
  };

  const handleSelectAll = () => {
    if (isCheckAll) {
      setIsCheck([]);
    } else {
      setIsCheck(teamList.map((u) => u.email));
    }
    setIsCheckAll(!isCheckAll);
  };

  // Keep "select all" in sync
  useEffect(() => {
    setIsCheckAll(isCheck.length === teamList.length && teamList.length > 0);
  }, [isCheck, teamList]);

  // Build table rows, skipping any bad entries
  const rows = teamList.reduce<any[]>((acc, member) => {
    try {
      if (!isValidCsvUser(member)) throw new Error("Invalid user");
      const email = member.email.includes("@sneakysneaky.ua") ? "" : member.email;

      acc.push({
        checkbox: (
          <Checkbox
            key={member.email}
            id={member.email}
            checked={isCheck.includes(member.email)}
            onChange={handleCheckboxChange}
            onClick={(e) => e.stopPropagation()}
            value={Object.values(member)}
          />
        ),
        firstName: member.firstname,
        lastName: member.lastname,
        email,
      });
    } catch {
      // skip malformed row
    }
    return acc;
  }, []);

  const onSubmit = async () => {
    const selectedUsers = teamList.filter((u) => isCheck.includes(u.email));
    if (selectedUsers.length === 0) return;

    trackEvent("learner-import");

    const componentId = PARTICIPANTS_CSV_IMPORT_COMPONENT;
    try {
      if (selectedUsers.length === 1) {
        await dispatch(
          inviteExternalEnduserWithoutProject({
            componentId,
            ...selectedUsers[0],
          }),
        );
        dispatch(
          setAlert({
            type: "success",
            msg: t("alerts.participantInvitedSuccessfully"),
          }),
        );
      } else {
        await dispatch(
          bulkInviteEndusersWithoutProject({
            componentId,
            endusers: selectedUsers,
          }),
        );
        dispatch(
          setAlert({
            type: "success",
            msg: t("alerts.participantsInvitedSuccessfully"),
          }),
        );
      }
      navigate("/learners");
    } catch (err) {
      console.error("Invite failed:", err);
      dispatch(
        setAlert({
          type: "error",
          msg: t("alerts.inviteFailed"),
        }),
      );
    }
  };

  const cancelForm = () => {
    setIsCheck([]);
    navigate("/learners/new");
  };

  const colHeaders = [t("general.firstname"), t("general.lastname"), t("general.email")];

  return (
    <IsLoading componentId={PARTICIPANTS_CSV_IMPORT_COMPONENT} showSuccess={false}>
      <div className="flex flex-col w-full h-full">
        <div className="flex w-full h-full">
          <div className="w-1/4 mt-8">
            <AvailableFeatureCounter
              className="w-80 h-28"
              currentNumberOfAvailable={currentNumberOfAvailableParticipants}
              totalNumberOfAvailable={totalNumberOfAvailableParticipants}
              text={t("pages.participants.participants")}
            />
          </div>

          <form
            className="h-full w-1/2 flex flex-col items-center"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex mb-3 mt-8">
              <h1 className="w-full font-bold text-xl text-center">
                {t("pages.participants.importCsv")}
              </h1>
            </div>

            <h2 className="mb-8 w-4/5 text-center">
              {t("pages.participants.importCsvAdditionalInfos")}
            </h2>

            {rows.length > 0 && (
              <IsLoading componentId={CONFIRM_CSV_COMPONENT} showSuccess={false}>
                <TableComp
                  rows={rows}
                  colHeaders={colHeaders}
                  selectFromListInTable
                  isAllCollaborators
                  isCollaboratorAndParticipant
                  style={{ maxHeight: `${tableHeight}px` }}
                  className="personalize-scroll-visible w-full"
                  key={tableHeight}
                  listTofilter={teamList}
                  setListOfItemsFilter={setTeamList}
                  checkAll={
                    <div className="flex items-center py-2" key="checkAll">
                      <input
                        type="checkbox"
                        id="select-all"
                        checked={isCheckAll}
                        onChange={handleSelectAll}
                      />
                    </div>
                  }
                />
              </IsLoading>
            )}
          </form>

          <div className="w-1/4" />
        </div>

        <FooterButtons
          cancelText={t("general.cancel")}
          cancelCb={cancelForm}
          nextText={t("general.invite")}
          nextCb={onSubmit}
          nextDisabled={
            isOverLimit ||
            isCheck.length === 0 ||
            participants.length + isCheck.length >
              (participantsFeature ? participantsFeature.QuantityCurrent + participants.length : 0)
          }
          nextDisabledTooltipText={deduceNextDisabledTooltip()}
        />
      </div>
    </IsLoading>
  );
};

export default ParticipantsCsvImport;
